import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

/**
 * Line chart with BASELINE (first point) and GOAL (last point) end-caps,
 * inspired by the "Daily Active Users" reference image.
 *
 * Renders the recharts LineChart inside a relative wrapper and paints two
 * absolute-positioned circular badges above the first & last data points.
 * Vertical drop lines fall from each badge to its dot.
 *
 * Props:
 *   data:            array of { label: string, value: number, ... }
 *   valueKey:        which key in each row is the primary line (default "value")
 *   height:          chart pixel height (default 260)
 *   baselineLabel:   text inside the left circle (default "BASELINE")
 *   goalLabel:       text inside the right circle (default "GOAL")
 *   baselineColor:   ring/stem color for baseline (default "#F9AE7C")
 *   goalColor:       ring/stem color for goal (default "#7DD3B0")
 *   lineColor:       chart line color (default "#DFF0E5")
 *   formatBadge:     (n) => string for the value inside each badge
 *   yTickFormatter:  (n) => string for the y axis
 *   yLabel:          text formatter for tooltip
 *   goalOverride:    optional number to display in goal badge instead of last value
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const EndCappedLineChart = ({
    data,
    valueKey = "value",
    height = 260,
    baselineLabel = "BASELINE",
    goalLabel = "GOAL",
    baselineColor = "#B36CFF",
    goalColor = "#0AFFB0",
    lineColor = "#0AFFB0",
    formatBadge = (n) => `${Math.round(n / 1000)}k`,
    yTickFormatter,
    tooltipFormatter,
    tooltipLabelFormatter,
    goalOverride,
    dataTestId,
}) => {
    const chart = useMemo(() => {
        if (!data || data.length === 0) return null;
        const values = data.map((d) => Number(d[valueKey]) || 0);
        const first = values[0];
        const last = values[values.length - 1];
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const goalVal = goalOverride ?? last;
        // Pad domain so both end circles + labels stay clear of grid
        const pad = (maxVal - minVal) * 0.15 || Math.abs(maxVal) * 0.15 || 10;
        return {
            first, last, minVal, maxVal, goalVal,
            domain: [Math.max(0, minVal - pad), maxVal + pad],
        };
    }, [data, valueKey, goalOverride]);

    if (!chart) return null;

    // Baseline sits at data[0], goal sits at data[last].
    // Positions computed with plain % of container width (since XAxis is
    // "category" spaced evenly). This matches recharts' default point placement.
    const lastIdx = data.length - 1;
    // Recharts default margin: left ~ 60px (YAxis), right ~ 10px.
    // We'll compute using CSS calc via inline style — a small overlay does the trick.

    return (
        <div
            className="relative"
            style={{ height: height + 40 }}
            data-testid={dataTestId}
        >
            {/* Header spacing for end-cap circles (they float above the plot area) */}
            <div style={{ height: 40 }} aria-hidden="true" />
            <div style={{ height }} className="relative">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 8, right: 24, left: 8, bottom: 4 }}
                    >
                        <defs>
                            <linearGradient id="ecl-line" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor={baselineColor} />
                                <stop offset="100%" stopColor={goalColor} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            strokeDasharray="0"
                            stroke="hsl(var(--border))"
                            vertical={false}
                            strokeOpacity={0.55}
                        />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                            padding={{ left: 24, right: 24 }}
                        />
                        <YAxis
                            tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={yTickFormatter}
                            width={48}
                        />
                        <Tooltip
                            formatter={tooltipFormatter}
                            labelFormatter={tooltipLabelFormatter}
                            contentStyle={{
                                background: "hsl(var(--card))",
                                border: "1px solid hsl(var(--border))",
                                borderRadius: 12,
                                color: "hsl(var(--foreground))",
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey={valueKey}
                            stroke="url(#ecl-line)"
                            strokeWidth={2.4}
                            dot={{ r: 3.5, fill: "#FFFFFF", stroke: lineColor, strokeWidth: 2 }}
                            activeDot={{ r: 5.5, fill: goalColor, stroke: "#FFFFFF", strokeWidth: 2 }}
                            isAnimationActive
                            animationDuration={1200}
                            animationEasing="ease-out"
                        />
                    </LineChart>
                </ResponsiveContainer>

                {/* End-cap circles — floating above the plot */}
                <BadgeCap
                    side="left"
                    color={baselineColor}
                    label={baselineLabel}
                    value={formatBadge(chart.first)}
                    testId="endcap-baseline"
                />
                <BadgeCap
                    side="right"
                    color={goalColor}
                    label={goalLabel}
                    value={formatBadge(chart.goalVal)}
                    testId="endcap-goal"
                />
            </div>
        </div>
    );
};

const BadgeCap = ({ side, color, label, value, testId }) => {
    const isLeft = side === "left";
    // Approximate x placement: matches recharts' first/last category tick with
    // XAxis padding {left:24, right:24} and YAxis width 48.
    const style = isLeft
        ? { left: 66 } // 48 (YAxis) + 24 (padding) - half circle (~28)
        : { right: 34 }; // 24 (right margin) + 24 (padding) - half circle
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.6, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: isLeft ? 0.1 : 0.25 }}
            className="absolute pointer-events-none"
            style={{ ...style, top: -40 }}
            data-testid={testId}
        >
            {/* The circle */}
            <div
                className="grid place-items-center rounded-full border-[3px] px-2 py-1 min-w-[86px] text-center"
                style={{
                    borderColor: color,
                    background: "hsl(var(--card))",
                    boxShadow: `0 6px 22px -8px ${color}66`,
                }}
            >
                <div
                    className="text-[9px] font-bold tracking-[0.2em] uppercase"
                    style={{ color }}
                >
                    {label}
                </div>
                <div
                    className="font-display font-extrabold text-lg leading-none tabular-nums mt-0.5"
                    style={{ color }}
                >
                    {value}
                </div>
            </div>
            {/* Vertical drop line */}
            <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                    top: "100%",
                    width: 2,
                    height: 22,
                    background: `linear-gradient(to bottom, ${color}, ${color}00)`,
                }}
            />
            {/* Tiny anchor circle at the bottom of the drop line */}
            <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full"
                style={{
                    top: "calc(100% + 20px)",
                    width: 8,
                    height: 8,
                    background: "hsl(var(--card))",
                    border: `2px solid ${color}`,
                }}
            />
        </motion.div>
    );
};

export default EndCappedLineChart;
